import { CommitUploadSessionRequest, CPS_NOTIFICATION_TYPE } from '@cs-cms/types';
import { FileHelper } from '@cs-cms/utils';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalizationContext } from '../../contexts/GlobalizationContext';
import { useSearchContext } from '../../contexts/SearchContext';
import ServicesContext from '../../contexts/ServicesContext';
import { mutateDocumentsKeys } from '../../mutationKeys/documents';
import { searchKeys } from '../../queryKeys/search';
import { useTranslation } from '../globalization/useTranslation';
import { useNotificationContext } from '../useNotificationContext';

export function useCommitUploadSession(): UseMutationResult<string, unknown, { dto: CommitUploadSessionRequest }> {
  const servicesCtx = useContext(ServicesContext);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();
  const { updateCenterNotification, showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const commitUploadSession = (data: { dto: CommitUploadSessionRequest }): Promise<string> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.commitUploadSession(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.commitUploadSession,
    mutationFn: (data: { dto: CommitUploadSessionRequest }) => commitUploadSession(data),
    onSuccess: async (result, variables) => {
      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }

      const fileName = variables.dto.fileName;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      updateCenterNotification(notificationId, {
        id: uuidv4(),
        type: 'success',
        title: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentSuccessTitle', {
          fileName
        }),
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentSuccessMessage'),
        progress: {
          current: 100,
          percent: 100,
          total: variables.dto.lastChunkIndex
        }
      });
    },
    onError: (data, variables) => {
      showToast({
        type: CPS_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedTitle')
      });

      const fileName = variables.dto.fileName;
      const notificationId = FileHelper.getFileNotificationId(fileName);

      updateCenterNotification(notificationId, {
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.uploadDocument.uploadDocumentFailedMessage', {
          fileName: variables.dto.fileName
        }),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
